import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {TransitionGroup, CSSTransition} from 'react-transition-group';

import Logo, {BRAND_TYPES as LOGO_BRANDS, LOGO_TYPES} from '@stubhub/react-logo';
import NavFilter from '@stubhub/react-nav-filter';
import SearchBar from '@stubhub/react-search-component';
import {connect} from '@stubhub/react-store-provider';

import getNavConfig from './nav-config';
import controller from './nav-controller';
import NavMenu from './nav-menu';
import messages from './nav-messages';

import '../styles/nav.scss';

/* istanbul ignore next*/
const NavItemGroup = ({
  id,
  items,
  activeItem,
  onItemClick,
  onOptionClick,
  onClickOutside,
  onMenuHover,
  onCategoryHover,
  screenSize,
  withGeoFilter,
}) => {
  return (
    <ul className={`mh__nav-item-group mh__nav-item-group__${id}`}>
      {items.map((menu) => {
        const href = menu.href || menu.webURI;

        return (
          <li className={`mh__nav-item mh__nav-item-${menu.id}`} key={menu.id}>
            <NavMenu
              className={menu.key}
              {...menu}
              href={href}
              onItemClick={onItemClick}
              onOptionClick={onOptionClick}
              onClickOutside={onClickOutside}
              onMenuHover={onMenuHover}
              onCategoryHover={onCategoryHover}
              active={menu.id === activeItem}
              activeMenuItem={activeItem}
              screenSize={screenSize}
              buttonWrapper={id === 'actions'}
              withGeoFilter={withGeoFilter}
            />
          </li>
        );
      })}
    </ul>
  );
};

NavItemGroup.propTypes = {
  /** The id of the group */
  id: PropTypes.string,

  /** The items to display */
  items: PropTypes.array,

  /** The active item */
  activeItem: PropTypes.string,

  /** Callback function when an item is clicked */
  onItemClick: PropTypes.func,

  /** Callback function when an option is clicked */
  onOptionClick: PropTypes.func,

  /** Callback function when clicked outside of the list */
  onClickOutside: PropTypes.func,

  /** Callback function when a menu item is hovered */
  onMenuHover: PropTypes.func,

  /** Callback function when a category is hovered */
  onCategoryHover: PropTypes.func,

  /** Screen size */
  screenSize: PropTypes.string,

  /** Flag to indicate if the geo filter is enabled */
  withGeoFilter: PropTypes.bool,
};

export class HeaderNav extends React.Component {
  static contextTypes = {
    globalRegistry: PropTypes.object,
    cookies: PropTypes.object,
  };

  constructor(props, context) {
    super(props);

    const {deviceType} = props;
    const {globalRegistry} = context;
    const isMobile = deviceType === 'phone' || deviceType === 'tablet';
    const isEnabled =
      globalRegistry && typeof globalRegistry.getFeatureValue === 'function'
        ? globalRegistry.getFeatureValue.bind(globalRegistry)
        : () => undefined;

    this.enableSubCatNav = isEnabled('common.enableSubCatNav');
    this.exposeExpandedNavMenu = isEnabled('common.exposeExpandedNavMenu');
    this.debugSubCatNavUK = isEnabled('common.debugSubCatNavUK');
    this.enableBulletLogin = isEnabled('homepage.enableLogin');
    this.hasDiscoveryVipCategory = isEnabled('common.header.VipCategory');
    this.hasDiscoveryCitiesCategory = isMobile
      ? isEnabled('common.header.mwebCitiesCategory')
      : isEnabled('common.header.desktopCitiesCategory');
    this.useAltBrand = isEnabled('common.useAltBrand');
  }

  addItemAtPath(items, path = [], value = {}) {
    if (path && path.length) {
      const [key] = path;
      path = path.slice(1);
      if (items && Array.isArray(items)) {
        items.forEach((item) => {
          if (item && item.id === key && item.items) {
            this.addItemAtPath(item.items, path, value);
          }
        });
      }
    } else {
      items.push(value);
    }
  }

  formatMessage(item, intl) {
    item.label = intl.formatMessage(messages[item.id]);
    if (messages[`${item.id}.toggle`]) {
      item.toggleLabel = intl.formatMessage(messages[`${item.id}.toggle`]);
    }
    if (item.items) {
      item.items.forEach((sub) => {
        this.formatMessage(sub, intl);
      });
    }
  }

  resetNavMenuState() {
    const {onNavMenuStateChange, setActiveMenuKey, activeMenuKey} = this.props;

    if (activeMenuKey !== null) {
      setActiveMenuKey(null);
    }

    onNavMenuStateChange(false);
  }

  updateNavMenuState(key) {
    const {activeMenuKey, setActiveMenuKey, onNavMenuStateChange} = this.props;

    if (!activeMenuKey || activeMenuKey !== key) {
      setActiveMenuKey(key);

      onNavMenuStateChange(true);
    } else {
      // The key is currently active, make it inactive
      this.resetNavMenuState();
    }
  }

  clearUserAuth = () => {
    if (typeof window !== 'undefined' && window.location) {
      const domain = window.location.host.split('www').pop();
      const {signOutUser} = this.props;

      signOutUser(domain)
        .then(() => {
          // Console logs showed that this line is executing, but coverage may have issue with the promise
          /* istanbul ignore next */
          this.resetNavMenuState();

          if (/^\/my\//i.test(window.location.pathname)) {
            window.location.assign('/');
          }

          return '';
        })
        .catch({});
    }
  };

  onItemClick = (key, items) => {
    if (key === 'search') {
      /**
       * Handle Search Button Click
       *
       */
      const {showSearchBarModal} = this.props;
      showSearchBarModal && /* istanbul ignore next */ showSearchBarModal(); // Ignore Defensive Coding for `showSearchBarModal` Function.
      this.resetNavMenuState();
    } else if (items) {
      // In these cases, there are nav menu items, we need to show/hide them
      this.updateNavMenuState(key);
    }
  };

  onOptionClick = (key) => {
    const {screenSize} = this.props;

    if (key === 'logout') {
      this.clearUserAuth();
    }

    // Without this check, the mweb sell dropdown menu resets the nav state before it can expand
    if (key === 'sell' && screenSize === 'small') {
      return;
    }

    // Doesn't matter what option was clicked, reset nav menu state
    this.resetNavMenuState();
  };

  onClickOutside = () => {
    // Reset nav menu state
    this.resetNavMenuState();
  };

  onMenuHover = (key, items) => {
    const {activeMenuKey, setActiveMenuKey} = this.props;

    /*
     * The sell & profile keys are to enable the dropdown on hover in desktop. Removing it still allows them to open
     * in desktop but makes the mobile menu fail, immediately closing after being opened
     */
    if (!activeMenuKey && ['sell', 'profile'].includes(key) && items) {
      setActiveMenuKey(key);
    }
  };

  // Need subCatNav feature toggle to ensure hover doesn't get stuck on category when feature off
  onCategoryHover = (key) => {
    const {activeMenuKey, setActiveMenuKey} = this.props;

    if (this.enableSubCatNav && !activeMenuKey) {
      setActiveMenuKey(key);
    }
  };

  render() {
    const {
      intl,
      onModalSearchActiveChanged,
      screenSize,
      homepage,
      categorypage,
      performerpage,
      geographypage,
      venuepage,
      langSelector,
      searchpage,
      start,
      end,
      onlyNavSecondary,
      noNavSecondary,
      deviceType,
      multiLayeredMenu,
      isSearchAutoFocus,
      searchBarStyle,
      subCategories,
      isModalSearchActive,
    } = this.props;
    const {globalRegistry} = this.context;
    const isSessionAuthEnabled = globalRegistry.getFeatureValue('common.sessionAuthEnabled');
    const storeId = this.debugSubCatNavUK ? 2 : globalRegistry.getStoreId(); // OJO Debug only!!
    const locale = globalRegistry.getCurrentLocale();
    const enableMultiLayeredMenu = deviceType === 'phone' && multiLayeredMenu && this.exposeExpandedNavMenu;
    const withGeoFilter = homepage || categorypage || searchpage || geographypage || venuepage;
    const data = getNavConfig(
      storeId,
      locale,
      this.enableBulletLogin,
      this.hasDiscoveryVipCategory,
      this.hasDiscoveryCitiesCategory,
      enableMultiLayeredMenu,
      isSessionAuthEnabled
    );
    this.configData = data;

    const {discovery} = data;
    const {actions} = data;
    if (this.exposeExpandedNavMenu && multiLayeredMenu && multiLayeredMenu.discovery) {
      const {discovery: multiLayeredDiscovery} = multiLayeredMenu;
      // Safe check for multiLayeredDiscovery
      multiLayeredDiscovery.forEach((item) => {
        const index = discovery.findIndex((discoveryItem) => discoveryItem.id === item.id);
        if (index !== -1) {
          discovery[index] = item;
        } else {
          // Add new discovery items that don't exist in the base config
          discovery.push(item);
        }
      });
      // discovery = multiLayeredDiscovery;
      this.configData.discovery = discovery;
    }

    /**
     * Add search button, if needed, to appropriate menu
     *
     */
    const searchNavItem = {
      id: 'search',
      type: 'menu',
    };

    if (searchBarStyle === 'modal') {
      // Need to add search button
      if (screenSize === 'small') {
        actions.unshift(searchNavItem);
      }
    }

    if (this.hasDiscoveryCitiesCategory && subCategories?.cities) {
      this.addItemAtPath(actions, ['menu', 'menu-category'], {id: 'cities', type: 'menu', items: []});
    }

    if (this.hasDiscoveryVipCategory && subCategories?.vip) {
      this.addItemAtPath(actions, ['menu', 'menu-category'], {id: 'vip', type: 'menu', items: []});
    }

    // Add labels
    for (const key in data) {
      const group = data[key];
      group.forEach((item) => {
        this.formatMessage(item, intl);
      });
    }

    if (this.hasDiscoveryCitiesCategory && subCategories?.cities) {
      subCategories.cities.forEach((item, index) => {
        if (item && item.name && item.webURI) {
          this.addItemAtPath(actions, ['menu', 'menu-category', 'cities'], {
            id: index,
            type: 'link',
            label: item.name,
            href: item.webURI,
          });
        }
      });
    }

    if (this.hasDiscoveryVipCategory && subCategories?.vip) {
      subCategories.vip.forEach((item, index) => {
        if (item && item.name && item.webURI) {
          this.addItemAtPath(actions, ['menu', 'menu-category', 'vip'], {
            id: index,
            type: 'link',
            label: item.name,
            href: item.webURI,
          });
        }
      });
    }

    let navCls = 'mh__nav-top';
    if (isModalSearchActive) {
      navCls += ' mh__nav--searchActive';
    }

    const {activeMenuKey} = this.props;

    const logoBrand = this.useAltBrand ? LOGO_BRANDS.ALTERNATIVE : LOGO_BRANDS.DEFAULT;

    const navTop = !onlyNavSecondary ? (
      <nav className={navCls}>
        <div className="mh__nav-primary">
          <Logo type={LOGO_TYPES.MERCH} brand={logoBrand} onItemClick={this.onItemClick} />
          {!isModalSearchActive && (
            <NavItemGroup
              id="actions"
              items={actions}
              activeItem={activeMenuKey}
              onItemClick={this.onItemClick}
              onOptionClick={this.onOptionClick}
              onClickOutside={this.onClickOutside}
              onMenuHover={this.onMenuHover}
              screenSize={screenSize}
              withGeoFilter={withGeoFilter}
            />
          )}
        </div>
      </nav>
    ) : null;

    const navSearch = !onlyNavSecondary ? (
      <div className="mh__nav-search">
        <TransitionGroup>
          <CSSTransition classNames="HeaderSearchBar" timeout={{enter: 500, exit: 500}}>
            <SearchBar
              searchStyle={searchBarStyle}
              onModalSearchActiveChanged={onModalSearchActiveChanged}
              suggestVariant={7}
              merchClassName="MerchHeader"
              isSearchAutoFocus={isSearchAutoFocus}
            />
          </CSSTransition>
        </TransitionGroup>
      </div>
    ) : null;

    let navSecondary = null;
    const noDateSelector =
      (!noNavSecondary && screenSize === 'small') ||
      (homepage && !globalRegistry.getFeatureValue('homepage.enableDateFilter'));

    if (!noNavSecondary && !(!(homepage || langSelector || searchpage) && screenSize === 'small')) {
      const navFilter = (
        <NavFilter
          {...{
            className: 'mh__nav-secondary',
            globalRegistry,
            screenSize,
            homepage,
            categorypage,
            performerpage,
            geographypage,
            venuepage,
            langSelector,
            searchpage,
            noDateSelector,
            start,
            end,
          }}
        />
      );
      navSecondary = (
        <div className="mh__nav-secondary">
          {!isModalSearchActive && (
            <NavItemGroup
              id="discovery"
              items={discovery}
              activeItem={activeMenuKey}
              onItemClick={this.onItemClick}
              onOptionClick={this.onOptionClick}
              onClickOutside={this.onClickOutside}
              onMenuHover={this.onMenuHover}
              onCategoryHover={this.onCategoryHover}
              screenSize={screenSize}
              withGeoFilter={withGeoFilter}
            />
          )}
          {navFilter}
        </div>
      );
    }

    return (
      <div className="mh__nav-container">
        <div className="mh__nav-wrapper">
          {navTop}
          {navSearch}
          {navSecondary}
        </div>
      </div>
    );
  }
}

HeaderNav.propTypes = {
  /** React Intl object */
  intl: PropTypes.object.isRequired,

  /** Device type */
  deviceType: PropTypes.string,

  /** Function to set the active menu key */
  setActiveMenuKey: PropTypes.func,

  /** Function to sign out the user */
  signOutUser: PropTypes.func,

  /** Nav filter start date */
  start: PropTypes.string,

  /** Nav filter end date */
  end: PropTypes.string,

  /** Multi-layered menu */
  multiLayeredMenu: PropTypes.object,

  /** Sub categories */
  subCategories: PropTypes.object,

  /** Function to show the search bar modal */
  showSearchBarModal: PropTypes.func,

  /** Search bar style */
  searchBarStyle: PropTypes.string.isRequired,

  /** Flag to indicate if the modal search is active */
  isModalSearchActive: PropTypes.bool,

  /** Screen size */
  screenSize: PropTypes.string,

  /** Callback when the modal search active changed */
  onModalSearchActiveChanged: PropTypes.func,

  /** Active menu key */
  activeMenuKey: PropTypes.string,

  /** Flag to indicate if the search bar should autofocus */
  isSearchAutoFocus: PropTypes.bool,

  /** Flag to indicate if the page is the homepage */
  homepage: PropTypes.bool,

  /** Flag to indicate if the page is the category page */
  categorypage: PropTypes.bool,

  /** Flag to indicate if the page is the performer page */
  performerpage: PropTypes.bool,

  /** Flag to indicate if the page is the geography page */
  geographypage: PropTypes.bool,

  /** Flag to indicate if the page is the venue page */
  venuepage: PropTypes.bool,

  /** Flag to indicate language selector is enabled */
  langSelector: PropTypes.bool,

  /** Flag to indicate if the page is the search page */
  searchpage: PropTypes.bool,

  /** Whether to show only secondary nav */
  onlyNavSecondary: PropTypes.bool,

  /** Whether to show secondary nav */
  noNavSecondary: PropTypes.bool,

  /** Callback function when the nav menu state changes */
  onNavMenuStateChange: PropTypes.func,
};

HeaderNav.defaultProps = {
  searchBarStyle: 'expanded',
  isModalSearchActive: false,
  screenSize: 'large',
  activeMenuKey: null,
};

export default connect(controller)(injectIntl(HeaderNav));
